import type { InitOptions, i18n } from 'i18next';
import LocizeBackend, { type LocizeBackendOptions } from 'i18next-locize-backend';
import type { CustomRequestOptions, RequestCallback } from 'i18next-locize-backend';
import { LRUCache } from 'lru-cache';
import ky from 'ky';

import { publicConfig } from '../utils/config';

const { LOCIZE, LANGUAGES } = publicConfig();

const [fallbackLng] = LANGUAGES;
const defaultNS = 'dtcclient';

const lru = new LRUCache<string, object>({ ttl: 1000 * LOCIZE.CACHE_TTL_SEC, max: 1 });

export function purgeCache() {
  lru.clear();
}

export function init(i18nextInstance: i18n, options: InitOptions<LocizeBackendOptions>) {
  if (LOCIZE.PROJECT_ID) i18nextInstance.use(LocizeBackend);

  const genericOptions = getOptions();

  return i18nextInstance.init({
    ...genericOptions,
    ...options,
    backend: {
      ...genericOptions.backend,
      ...options.backend,
      allowedAddOrUpdateHosts: () => LOCIZE.SAVE_MISSING,
      async request(o: CustomRequestOptions, cb: RequestCallback) {
        try {
          switch (o.method) {
            case 'GET': {
              const cached = lru.get(o.url);
              if (cached) {
                cb(undefined, {
                  status: 200,
                  data: cached,
                });
                return;
              }

              const res = await ky.get<object>(o.url, {
                cache: 'no-store',
              });

              const data = await res.json();
              lru.set(o.url, data);
              cb(undefined, {
                status: res.status,
                data,
              });
              break;
            }
            case 'POST': {
              await ky.post(o.url, {
                json: o.body,
                headers: o.headers,
              });
              break;
            }
            default: {
              throw new Error('UNSUPPORTED_METHOD');
            }
          }
        } catch (e) {
          cb(undefined, {
            status: 200,
            data: lru.get(o.url) ?? {},
          });
        }
      },
    },
  });
}

export function getOptions(lng = fallbackLng) {
  return {
    debug: LOCIZE.DEBUG,
    supportedLngs: LANGUAGES,
    saveMissing: LOCIZE.SAVE_MISSING,
    fallbackLng,
    lng,
    fallbackNS: defaultNS,
    defaultNS,
    ns: defaultNS,
    returnNull: false,
    backend: {
      projectId: LOCIZE.PROJECT_ID,
      apiKey: LOCIZE.API_KEY,
      referenceLng: fallbackLng,
      version: LOCIZE.VERSION,
    } as LocizeBackendOptions,
  } satisfies InitOptions;
}
